import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useHasMenu } from 'auth/access';
import { ConfirmationModal, MonthYearPickerAction, SectionTitle } from 'modules/shared/components';
import { Table } from 'modules/shared/components/data-display';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import {
  generatePayrollMonth,
  getPayroll,
  getSalaryRelease,
  PayrollRow,
  releaseSalaries,
  SalaryRelease,
} from '../../hr.api';
import AdvanceSalaryModal from '../components/AdvanceSalaryModal';
import PayrollRowEditModal from '../components/PayrollRowEditModal';
import {
  hours,
  isMonthLocked,
  lastCompletedMonth,
  money,
  monthLabel,
  monthTotal,
  releaseChipColor,
  unverifiedCount,
  verifiedChip,
} from '../payroll.utils';

/**
 * The salary board: one month of payroll, priced by the API, corrected here, then released
 * to Accounts.
 *
 * This screen was a "coming soon" placeholder over a prototype that ran on mock data, a
 * hard-coded ₹300 hourly rate and a Release button with no handler. Every figure below now
 * comes from `GET /api/hr/attendance/{year}/{month}`; nothing is calculated in the browser.
 */
export default function SalaryBoardPage() {
  // Rewriting the hours needs the attendance register's Edit; others may read the board.
  const canEdit = useHasMenu('hr.attendance-register', 'EDIT');

  const [period, setPeriod] = useState(lastCompletedMonth);
  const [rows, setRows] = useState<PayrollRow[]>([]);
  const [release, setRelease] = useState<SalaryRelease | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<PayrollRow | null>(null);
  const [advanceFor, setAdvanceFor] = useState<PayrollRow | null>(null);
  const [confirmRelease, setConfirmRelease] = useState(false);

  const { year, month } = period;
  const locked = isMonthLocked(release);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [payroll, status] = await Promise.all([
        getPayroll(year, month),
        getSalaryRelease(year, month),
      ]);
      setRows(payroll);
      setRelease(status);
    } catch (err) {
      setError(
        logError({ error: err, message: 'Failed to load the salary board', code: 'SALARY_BOARD_FAILED' })
      );
      setRows([]);
      setRelease(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOpenMonth = async (recalculate: boolean) => {
    setBusy(true);
    try {
      const result = await generatePayrollMonth(year, month, recalculate);
      toast.success(
        recalculate
          ? `Recalculated ${result.updated} row(s) for ${monthLabel(year, month)}`
          : `Opened ${monthLabel(year, month)} — ${result.created} row(s) created`
      );
      await load();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to open the payroll month', code: 'PAYROLL_GENERATE_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRelease = async () => {
    setBusy(true);
    try {
      const updated = await releaseSalaries({ year, month });
      setRelease(updated);
      setConfirmRelease(false);
      toast.success(`${monthLabel(year, month)} released to Accounts`);
      await load();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to release salaries', code: 'SALARY_RELEASE_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  // A saved row comes back fully re-priced, so it replaces the old one rather than refetching.
  const handleRowSaved = (updated: PayrollRow) =>
    setRows((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));

  const total = useMemo(() => monthTotal(rows), [rows]);
  const unverified = useMemo(() => unverifiedCount(rows), [rows]);

  const columns = useMemo(
    () => [
      { id: 'employee_name', dataKey: 'employee_name', label: 'Employee', align: 'left' as const, sortable: true },
      { id: 'department', dataKey: 'department', label: 'Department', align: 'left' as const, sortable: true },
      {
        id: 'days',
        dataKey: 'working_days',
        label: 'Days',
        align: 'center' as const,
        renderCell: (_value: unknown, row: PayrollRow) => `${row.working_days}/${row.total_days}`,
      },
      {
        id: 'payable_hours',
        dataKey: 'payable_hours',
        label: 'Payable hrs',
        align: 'center' as const,
        sortable: true,
        renderCell: (_value: unknown, row: PayrollRow) =>
          `${hours(row.payable_hours)}/${hours(row.standard_hours)}`,
      },
      { id: 'overtime_hours', dataKey: 'overtime_hours', label: 'OT', align: 'center' as const, renderCell: hours },
      { id: 'per_hour_rate', dataKey: 'per_hour_rate', label: 'Rate/hr', align: 'right' as const, renderCell: money },
      { id: 'gross_salary', dataKey: 'gross_salary', label: 'Gross', align: 'right' as const, sortable: true, renderCell: money },
      { id: 'incentive', dataKey: 'incentive', label: 'Incentive', align: 'right' as const, renderCell: money },
      { id: 'advance', dataKey: 'advance', label: 'Advance', align: 'right' as const, renderCell: money },
      { id: 'professional_tax', dataKey: 'professional_tax', label: 'PT', align: 'right' as const, renderCell: money },
      { id: 'tds', dataKey: 'tds', label: 'TDS', align: 'right' as const, renderCell: money },
      {
        id: 'net_payable',
        dataKey: 'net_payable',
        label: 'Net payable',
        align: 'right' as const,
        sortable: true,
        renderCell: (value: unknown) => <strong>{money(value)}</strong>,
      },
      { id: 'verified_by_hr', dataKey: 'verified_by_hr', label: 'HR', align: 'center' as const, renderCell: verifiedChip },
      ...(canEdit
        ? [
            {
              id: 'actions',
              dataKey: 'id',
              label: '',
              align: 'center' as const,
              renderCell: (_value: unknown, row: PayrollRow) => (
                <Stack direction='row' justifyContent='center'>
                  <Tooltip title={locked ? 'Month already paid' : 'Correct hours / verify'}>
                    <span>
                      <IconButton size='small' disabled={locked} onClick={() => setEditing(row)}>
                        <Icon icon='mdi:pencil-outline' width={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={locked ? 'Month already paid' : 'Pay an advance'}>
                    <span>
                      <IconButton size='small' disabled={locked} onClick={() => setAdvanceFor(row)}>
                        <Icon icon='solar:hand-money-linear' width={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              ),
            },
          ]
        : []),
    ],
    [canEdit, locked]
  );

  const status = release?.status ?? 'Unreleased';
  const released = Boolean(release?.is_salary_release_requested);

  return (
    <Stack gap={3}>
      <SectionTitle
        title='SALARY BOARD'
        action={
          <Stack direction='row' gap={2} alignItems='center' flexWrap='wrap'>
            <MonthYearPickerAction
              year={year}
              month={month - 1}
              onChange={({ year: y, month: m }: { year: number; month: number }) =>
                setPeriod({ year: y, month: m + 1 })
              }
            />
            <Chip size='small' label={status} color={releaseChipColor(status)} />
          </Stack>
        }
      />

      {error && (
        <Alert severity='error' action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Stack direction='row' gap={4} flexWrap='wrap' alignItems='center'>
        <Stack>
          <Typography variant='body2' color='text.secondary'>
            Month total
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>{money(total)}</Typography>
        </Stack>
        <Stack>
          <Typography variant='body2' color='text.secondary'>
            Employees
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>{rows.length}</Typography>
        </Stack>
        <Stack>
          <Typography variant='body2' color='text.secondary'>
            Awaiting HR sign-off
          </Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 600 }}>{unverified}</Typography>
        </Stack>

        <Stack direction='row' gap={2} sx={{ marginLeft: 'auto' }} flexWrap='wrap'>
          {canEdit && !locked && (
            <Button
              variant='outlined'
              disabled={busy || loading}
              onClick={() => handleOpenMonth(rows.length > 0)}
              title={rows.length > 0 ? 'Re-derives the rows not yet verified; verified rows keep their hours and are only re-priced.' : undefined}
            >
              {rows.length > 0 ? 'Recalculate month' : 'Open month'}
            </Button>
          )}
          {canEdit && (
            <Button
              variant='contained'
              disabled={busy || loading || locked || rows.length === 0}
              onClick={() => setConfirmRelease(true)}
            >
              {released ? 'Re-release salaries' : 'Release all salaries'}
            </Button>
          )}
        </Stack>
      </Stack>

      {locked && (
        <Alert severity='success'>
          {monthLabel(year, month)} has been paid. Its attendance and payslips are now a record and
          cannot be changed.
        </Alert>
      )}

      {!locked && released && (
        <Alert severity='info'>
          Sent to Accounts for {money(release?.total_amount)}. Edits made here re-price the release
          until it is paid.
        </Alert>
      )}

      {!loading && rows.length === 0 && (
        <Alert severity='warning'>
          No payroll rows exist for {monthLabel(year, month)} yet
          {canEdit ? ' — use "Open month" to create one row per employee on the books.' : '.'}
        </Alert>
      )}

      <Table
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage={`No payroll for ${monthLabel(year, month)}`}
        resetSortKey={`${year}-${month}`}
      />

      <PayrollRowEditModal
        open={Boolean(editing)}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={handleRowSaved}
      />

      <AdvanceSalaryModal
        open={Boolean(advanceFor)}
        row={advanceFor}
        onClose={() => setAdvanceFor(null)}
        onSaved={load}
      />

      <ConfirmationModal
        open={confirmRelease}
        onClose={() => setConfirmRelease(false)}
        loading={busy}
        icon='solar:hand-money-linear'
        title={`Release ${monthLabel(year, month)}?`}
        message={
          unverified > 0
            ? `${money(total)} for ${rows.length} employee(s). ${unverified} row(s) are not yet verified by HR.`
            : `${money(total)} for ${rows.length} employee(s) goes to Accounts, and payslips are written.`
        }
        confirmText='Release'
        onConfirm={handleRelease}
      />
    </Stack>
  );
}
